import type { OpportunityRelatedRecords } from '~/hooks/apis/offers/useGetOpportunityRelated';

import { pluralizeToString } from '~/utils/stringUtils';

import { egsRoomNames } from '../tabs/egs/egsModel';
import type { PreviousEgs } from '../tabs/egs/previousEgs';
import { type PreviousDealOptions, replaceWithPreviousRoomPricing } from '../tabs/packageOptions/previousDealOptions';
import type { OptionState, RoomAssetLink } from '../tabs/packageOptions/types';

const EGS_NOUN = 'extra guest surcharge';

export function egsRoomNamesAfterCopy(
  targetRelated: OpportunityRelatedRecords | undefined,
  optionState: OptionState,
  roomAssets: Array<RoomAssetLink>,
  copiedDealOptions: PreviousDealOptions | undefined,
): Array<string> {
  const roomsByCategory = copiedDealOptions
    ? replaceWithPreviousRoomPricing(optionState, copiedDealOptions).roomsByCategory
    : optionState.roomsByCategory;
  return egsRoomNames(targetRelated, roomsByCategory, roomAssets);
}

export type PreviousOpportunityChoice = {
  id: string;
  name: string;
  stage: string;
  closeDate: string;
};

export type CopySection = 'dealOptions' | 'surcharges' | 'extraGuestSurcharges';

export const COPY_SECTIONS: Array<{ key: CopySection; label: string; replaces: string }> = [
  { key: 'dealOptions', label: 'Deal Options', replaces: 'Package Options Option 1' },
  { key: 'surcharges', label: 'Surcharges', replaces: 'Surcharge periods' },
  { key: 'extraGuestSurcharges', label: 'Extra Guest Surcharges', replaces: 'Extra guest surcharges' },
];

export type CopyStep = 'opportunity' | 'sections' | 'egsRooms' | 'confirm';

export const COPY_STEP_LABELS: Record<CopyStep, string> = {
  opportunity: 'Opportunity',
  sections: 'What to copy',
  egsRooms: 'Extra guest surcharge rooms',
  confirm: 'Confirm',
};

export type CopyReadiness = {
  hasSource: boolean;
  selected: Set<CopySection>;
  isFetching: boolean;
  dealOptionRooms: number;
  egsRecords: number;
  everyEgsHasRooms: boolean;
};

export function copySteps(selected: Set<CopySection>): Array<CopyStep> {
  return selected.has('extraGuestSurcharges')
    ? ['opportunity', 'sections', 'egsRooms', 'confirm']
    : ['opportunity', 'sections', 'confirm'];
}

export function isStepComplete(step: CopyStep, readiness: CopyReadiness): boolean {
  const { hasSource, selected, isFetching, dealOptionRooms, egsRecords, everyEgsHasRooms } = readiness;
  switch (step) {
    case 'opportunity':
      return hasSource;
    case 'sections':
      return (
        selected.size > 0 &&
        !isFetching &&
        (!selected.has('dealOptions') || dealOptionRooms > 0) &&
        (!selected.has('extraGuestSurcharges') || egsRecords > 0)
      );
    case 'egsRooms':
      return everyEgsHasRooms;
    case 'confirm':
      return true;
  }
}

export function canCopy(readiness: CopyReadiness): boolean {
  return copySteps(readiness.selected).every((step) => isStepComplete(step, readiness));
}

type OpportunitySummary = Pick<App.OpportunityPage, 'id'> & {
  opportunity_info: Pick<App.OpportunityPage['opportunity_info'], 'name' | 'stagename'>;
  sales_and_forecast: Pick<App.OpportunityPage['sales_and_forecast'], 'closedate'>;
};

export function previousOpportunityChoices(
  opportunities: Array<OpportunitySummary>,
  currentOpportunityId: string,
): Array<PreviousOpportunityChoice> {
  return opportunities
    .filter((opportunity) => opportunity.id && opportunity.id !== currentOpportunityId)
    .map((opportunity) => ({
      id: opportunity.id,
      name: opportunity.opportunity_info.name,
      stage: opportunity.opportunity_info.stagename,
      closeDate: opportunity.sales_and_forecast.closedate,
    }))
    .sort((a, b) => (b.closeDate || '').localeCompare(a.closeDate || ''));
}

type CopyNote = { severity: 'info' | 'warning'; text: string };

export type CopySectionDetails = { summary?: string; emptyMessage?: string; notes: Array<CopyNote> };

function presentNotes(notes: Array<[CopyNote['severity'], string | undefined]>): Array<CopyNote> {
  return notes.flatMap(([severity, text]) => (text ? [{ severity, text }] : []));
}

export function dealOptionsNotes(previous: PreviousDealOptions, netCurrency: string): Array<CopyNote> {
  return presentNotes([
    ['info', ambiguousCellsNote(previous)],
    ['warning', mixedOccupancyNote(previous)],
    ['warning', otherCurrencyNetNote(previous, netCurrency)],
  ]);
}

export function egsNotes(previous: PreviousEgs, currency: string): Array<CopyNote> {
  return presentNotes([
    ['warning', egsBeddingNote(previous)],
    ['warning', egsNotCopiedNote(previous, currency)],
  ]);
}

export function dealOptionsSummary({ rooms }: PreviousDealOptions, lengthsOfStay: Array<number>): string {
  return `${pluralizeToString('room', rooms.length)} · LOS ${lengthsOfStay.map((nights) => `${nights}N`).join(', ')}`;
}

function ambiguousCellsNote({ ambiguousCells }: PreviousDealOptions): string | undefined {
  return ambiguousCells
    ? `Not copied: ${pluralizeToString('room and stay length combination', ambiguousCells)} with several Deal Options (for example other occupancies, solo or upgrades). Fill those in yourself.`
    : undefined;
}

export function mixedOccupancyNote({ mixedOccupancyRooms }: PreviousDealOptions): string | undefined {
  return mixedOccupancyRooms
    ? `Occupancy set to 2A, check it: ${pluralizeToString('room', mixedOccupancyRooms)} whose Deal Options name different occupancies.`
    : undefined;
}

function otherCurrencyNetNote({ otherCurrencyNetCells }: PreviousDealOptions, netCurrency: string): string | undefined {
  return otherCurrencyNetCells
    ? `Net prices not copied for ${pluralizeToString('room and stay length combination', otherCurrencyNetCells)}: their Deal Options cost in another currency than this Opportunity's ${netCurrency}.`
    : undefined;
}

export function egsSummary({ records }: PreviousEgs): string {
  return `${records.length} to copy`;
}

function egsBeddingNote({ beddingToChoose }: PreviousEgs): string | undefined {
  return beddingToChoose
    ? `Choose the bedding: ${pluralizeToString(EGS_NOUN, beddingToChoose)} whose Salesforce bedding covers several types.`
    : undefined;
}

function egsNotCopiedNote(
  { paidToHotel, otherCollectedBy, otherGuestType, otherCurrency }: PreviousEgs,
  currency: string,
): string | undefined {
  const reasons = [
    paidToHotel && `${paidToHotel} paid directly to the hotel`,
    otherCollectedBy && `${otherCollectedBy} with an empty or unknown collected by`,
    otherGuestType && `${otherGuestType} for a guest type the EGS tab has no category for`,
    otherCurrency && `${otherCurrency} with a cost in another currency than this Opportunity's ${currency}`,
  ].filter(Boolean);
  return reasons.length ? `Not copied, add them yourself: ${reasons.join(', ')}.` : undefined;
}

export function replaceWarning(selected: Set<CopySection>): string | undefined {
  const replaced = COPY_SECTIONS.filter((section) => selected.has(section.key)).map((section) => section.replaces);
  return replaced.length
    ? `This replaces the current ${replaced.join(', ')}. Anything already typed there will be lost.`
    : undefined;
}
